/**
 * Ids of everything the Long Night ruleset ("ledger") adds to a match: Superstitions, Carvings,
 * Stamps, Finishes, Curios, Omens, status effects and House Rules (docs/SHOP_DESIGN.md §9–§11,
 * LEDGER.MD). The engine resolves every one of them; the campaign layer only sells them.
 */

export const SUPERSTITIONS = [
  // Colors and cards
  'red-thread',
  'blue-bead',
  'four-leaf',
  'canary-feather',
  'odd-fellow',
  'even-keel',
  'snap',
  'bloodline',
  // Patterns
  'ladder-walker',
  'mirror-twin',
  'ribbon',
  'plumb-line',
  'compass-rose',
  'stairwell',
  'prism',
  'palindrome',
  'double-knot',
  'chain-letter',
  // Captures
  'horseshoe',
  'black-cat',
  'bottle-tree',
  'crows-feather',
  'butchers-hook',
  'skeleton-key',
  // Crownings
  'paper-crown',
  'penny-on-the-track',
  'homecoming',
  'black-mirror',
  'tallow-candle',
  // Epitaphs
  'rubbings',
  'mourning-bell',
  'gravediggers-spade',
  'widows-veil',
  'keepsake-locket',
  'seance-table',
  // Collateral and sacrifice
  'blood-money',
  'rag-and-bone',
  'debt-collector',
  'pawn-ticket',
  'sacrificial-lamb',
  // Thrift and money
  'coin-in-the-shoe',
  'misers-purse',
  'magpies-hoard',
  'early-bird',
  'piggy-bank',
  'golden-tooth',
  // Tempo
  'first-light',
  'last-orders',
  'spilled-salt',
  'seventh-son',
  'grandfather-clock',
  'thirteenth-step',
  'long-shadow',
  // The board
  'salt-line',
  'cornerstone',
  'crossroads',
  'phalanx',
  'tallow-stub',
  'long-way-home',
  // Chance
  'lucky-coin',
  'loaded-die',
  'wishbone',
  'rabbits-foot',
  // Copies
  'mimic-jar',
  'reliquary',
  'empty-chair',
  // Legendary
  'the-unredeemed',
  'the-last-pawn',
  'saints-finger',
  'pawn-kings-crown',
  'brokers-monocle',
] as const;
export type SuperstitionId = (typeof SUPERSTITIONS)[number];

export const CARVINGS = [
  'spurs',
  'hollow-base',
  'tusk',
  'pockets',
  'tin-heart',
  'gilt-base',
  'scribes-mark',
  'homing',
  'iron-shell',
  'lantern',
  'glutton',
  'dowser',
  'thorns',
  'many-lives',
  'bell-ringer',
  'crowned-head',
  'knights-gait',
  'comet',
  'twin-soul',
  'bellwether',
  'kite',
] as const;
export type CarvingId = (typeof CARVINGS)[number];

export const STAMPS = [
  'brass-stamp',
  'wax-seal',
  'coin-stamp',
  'double-stamp',
  'return-stamp',
  'glass-stamp',
  'lucky-stamp',
  'chalk-stamp',
  'ward-stamp',
  'tally-stamp',
] as const;
export type StampId = (typeof STAMPS)[number];

export const FINISHES = ['foil', 'gilt', 'pearl', 'ghost'] as const;
export type FinishId = (typeof FINISHES)[number];

/** Curios used at the table (USE_CURIO). Bench Curios are used between tables (campaign layer). */
export const TABLE_CURIOS = [
  'chalk',
  'hourglass',
  'pliers',
  'scissors',
  'bell-jar',
  'smelling-salts',
  'pocket-watch',
  'black-candle',
  'lodestone',
  'rag-doll',
  'planchette',
  'cracked-mirror',
  'spyglass',
  'matchbook',
] as const;
export type TableCurioId = (typeof TABLE_CURIOS)[number];

/** Tricks (docs/SHOP_DESIGN.md §10.7): cards that count as a link but move nothing. */
export const TRICKS = [
  'reverse',
  'gag',
  'swap',
  'shove',
  'chalk-line',
  'draw-two',
  'hex',
  'requiem',
  'tithe',
] as const;
export type TrickId = (typeof TRICKS)[number];

export const OMENS = [
  'candle',
  'gilt-tile',
  'salt-circle',
  'pillar',
  'well',
  'ink-pool',
  'chalk-x',
  'thorns',
  'altar',
  'trapdoor',
  'lost-ring',
  'draft',
  'mimic',
  'looking-glass',
  'brazier',
] as const;
export type OmenKind = (typeof OMENS)[number];
/** A Shrouded Omen as a side sees it before it is revealed. */
export type OmenViewKind = OmenKind | 'unknown';

export const PAWN_STATUSES = [
  'warded',
  'hasted',
  'kindled',
  'phased',
  'gilded',
  'pinned',
  'exposed',
  'cursed',
  'smoldering',
  'hollow',
  'heavy',
] as const;
export type PawnStatusKind = (typeof PAWN_STATUSES)[number];

export const HOUSE_RULES = [
  // Old Tallow
  'short-wick',
  'drip',
  'snuffed',
  // The Magpie
  'sticky-fingers',
  'gleam',
  'pickpocket',
  // The Widow
  'mourning-dress',
  'last-rites',
  'veiled',
  // The Broker
  'collateral-call',
  'foreclosure',
  // Patrons and the general pool
  'salted-earth',
  'tarnish',
  'heavy-chains',
  'no-repeats',
  'single-file',
  'toll',
  'iron-ranks',
  'short-hand',
  'rust',
  'heavy-toll',
  'clock',
  'warded-start',
  // The bosses' powers after Night III (horrors.ts)
  'storm',
  'turned-table',
  'switch',
  'lights-out',
  'blind-eye',
  'clean-slate',
] as const;
export type HouseRuleId = (typeof HOUSE_RULES)[number];

/** The bosses' powers after Night III: House Rules that act on the table itself (horrors.ts). */
export const HORRORS = [
  'storm',
  'turned-table',
  'switch',
  'lights-out',
  'blind-eye',
  'clean-slate',
] as const satisfies readonly HouseRuleId[];
export type HorrorId = (typeof HORRORS)[number];

/** Patterns and feats an Appraisal can raise (docs/SHOP_DESIGN.md §10.2). */
export const APPRAISAL_TARGETS = [
  'ladder',
  'echo',
  'streak',
  'stair',
  'prism',
  'mirror',
  'fullCircle',
  'none',
  'capture',
  'crown',
  'landing',
  'epitaph',
] as const;
export type AppraisalTarget = (typeof APPRAISAL_TARGETS)[number];

/** Patterns that count only once discovered in the run. */
export const HIDDEN_PATTERNS = ['stair', 'prism', 'mirror'] as const;
export type HiddenPattern = (typeof HIDDEN_PATTERNS)[number];

export const includes = <T extends string>(list: readonly T[], v: unknown): v is T =>
  typeof v === 'string' && (list as readonly string[]).includes(v);
